import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  ArrowLeft,
  MapPin,
  Mail,
  Globe,
  Star,
  CheckCircle,
  Loader2,
  Building2,
  Search,
  X,
  Send,
  Users,
  Clock,
  Shield,
  MessageSquare,
  ThumbsUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { getState } from '@/data/stateData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryNames = {
  hvac: "HVAC & Heating",
  plumbing: "Plumbing",
  electrical: "Electrical",
  home_repair: "Home Repair",
  roofing: "Roofing",
  landscaping: "Landscaping",
  auto_repair: "Auto Repair",
  septic: "Septic Services",
  dental: "Dental & Medical",
  restaurant: "Restaurant",
  fishing_charter: "Fishing Charter",
  sightseeing: "Sightseeing Tours",
  gym: "Gym & Fitness"
};

// Star rating component
const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <Star
            className={`${sizeClasses} ${
              star <= (hovered || rating)
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-slate-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function StateContractors() {
  const { stateCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stateData = getState(stateCode);
  
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(searchParams.get('industry') || '');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [contractorReviews, setContractorReviews] = useState(null);
  
  // Lead gate form
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [submittingLead, setSubmittingLead] = useState(false);
  
  // Inquiry form
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_needed: ''
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  
  // Review form
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 0,
    title: '',
    comment: '',
    service_used: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Check for existing access
  useEffect(() => {
    const accessToken = sessionStorage.getItem('contractor_directory_access');
    if (accessToken) {
      setHasAccess(true);
    }
  }, []);

  // Fetch contractors
  useEffect(() => {
    if (hasAccess && stateCode) {
      fetchContractors();
    } else {
      setLoading(false);
    }
  }, [hasAccess, stateCode, selectedIndustry]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const params = selectedIndustry ? `?industry=${selectedIndustry}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/contractors/by-state/${stateCode}${params}`);
      setContractors(response.data.contractors || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmittingLead(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contractors/lead-gate`, {
        name: leadForm.name,
        email: leadForm.email,
        state: stateCode
      });
      
      if (response.data.access_token) {
        // Store in memory only (sessionStorage clears on tab close for better security)
        sessionStorage.setItem('contractor_directory_access', response.data.access_token);
        setHasAccess(true);
        toast.success('Access granted!');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmittingLead(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!selectedContractor) return;
    
    setSubmittingInquiry(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contractors/inquiry`, {
        contractor_id: selectedContractor.contractor_id,
        ...inquiryForm
      });
      
      toast.success('Inquiry sent! The contractor will contact you soon.');
      setShowInquiryModal(false);
      setInquiryForm({ name: '', email: '', phone: '', message: '', service_needed: '' });
    } catch (error) {
      console.error('Inquiry error:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContractor || !reviewForm.rating) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmittingReview(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contractors/reviews`, {
        contractor_id: selectedContractor.contractor_id,
        ...reviewForm
      });
      
      toast.success('Thank you for your review!');
      setShowReviewModal(false);
      setReviewForm({ reviewer_name: '', reviewer_email: '', rating: 0, title: '', comment: '', service_used: '' });
      // Refresh contractors to show updated rating
      fetchContractors();
    } catch (error) {
      console.error('Review error:', error);
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const fetchReviews = async (contractor) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contractors/reviews/${contractor.contractor_id}`);
      setContractorReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const openInquiry = (contractor) => {
    setSelectedContractor(contractor);
    setShowInquiryModal(true);
  };

  const openReview = (contractor) => {
    setSelectedContractor(contractor);
    setShowReviewModal(true);
    fetchReviews(contractor);
  };

  if (!stateData) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">State Not Found</h1>
          <Button onClick={() => navigate('/us-demo')}>Back to Map</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/us-demo')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center font-bold">
                  {stateData.abbr}
                </div>
                <div>
                  <span className="font-bold text-lg">{stateData.name} Contractors</span>
                  <p className="text-xs text-slate-400">Find local service providers</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate('/contractors/register')}
              variant="outline"
              className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
            >
              List Your Business
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Lead Gate */}
        {!hasAccess ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-sky-400" />
              </div>
              <h1 className="text-2xl font-bold mb-3">Access Contractor Directory</h1>
              <p className="text-slate-400 mb-6">
                Enter your info to view contractors in {stateData.name}. 
                We'll also send you updates about new contractors in your area.
              </p>
              
              <form onSubmit={handleLeadSubmit} className="space-y-4 text-left" data-testid="lead-gate-form">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Your Name</label>
                  <Input
                    value={leadForm.name}
                    onChange={(e) => setLeadForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    required
                    className="bg-slate-800 border-slate-700"
                    data-testid="lead-gate-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Email Address</label>
                  <Input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    required
                    className="bg-slate-800 border-slate-700"
                    data-testid="lead-gate-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submittingLead}
                  className="w-full bg-sky-500 hover:bg-sky-600"
                  data-testid="lead-gate-submit"
                >
                  {submittingLead ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Please wait...
                    </>
                  ) : (
                    'View Contractors'
                  )}
                </Button>
              </form>
              
              <p className="text-xs text-slate-500 mt-4">
                By continuing, you agree to our Terms and Privacy Policy.
              </p>
            </div>
          </motion.div>
        ) : (
          /* Main Content */
          <>
            {/* State Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-8"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Service Providers in {stateData.name}</h1>
                  <p className="text-slate-400">{stateData.seasonalNotes}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <MapPin className="w-4 h-4" />
                  {stateData.majorCities.slice(0, 3).join(', ')}
                </div>
              </div>
            </motion.div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="h-10 px-4 rounded-lg bg-slate-800 border border-slate-700 text-white"
              >
                <option value="">All Industries</option>
                {Object.entries(industryNames).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              
              <div className="flex items-center text-sm text-slate-400">
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <span>{contractors.length} contractor{contractors.length !== 1 ? 's' : ''} found</span>
                )}
              </div>
            </div>

            {/* Contractors Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
              </div>
            ) : contractors.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Contractors Yet</h3>
                <p className="text-slate-400 mb-6">
                  Be the first to list your business in {stateData.name}!
                </p>
                <Button
                  onClick={() => navigate('/contractors/register')}
                  className="bg-sky-500 hover:bg-sky-600"
                >
                  Register Your Business
                </Button>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {contractors.map((contractor, index) => (
                  <motion.div
                    key={contractor.contractor_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      {/* Logo */}
                      {contractor.logo_url ? (
                        <img 
                          src={contractor.logo_url} 
                          alt={contractor.business_name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-lg font-bold text-sky-400 flex-shrink-0">
                          {contractor.business_name?.charAt(0) || '?'}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg truncate">{contractor.business_name}</h3>
                        <p className="text-sm text-slate-400">
                          {industryNames[contractor.industry] || contractor.industry}
                        </p>
                      </div>
                      
                      {contractor.is_verified && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-500/10 rounded text-xs text-green-400 flex-shrink-0">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </div>
                      )}
                    </div>
                    
                    {/* Rating Display */}
                    {contractor.rating && (
                      <div className="flex items-center gap-2 mb-3">
                        <StarRating rating={contractor.rating} size="sm" />
                        <span className="text-sm text-yellow-400">{contractor.rating}</span>
                        <span className="text-xs text-slate-500">({contractor.review_count} reviews)</span>
                      </div>
                    )}
                    
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-400">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        {contractor.city}, {contractor.state}
                      </div>
                      {contractor.phone && (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          {contractor.phone}
                        </div>
                      )}
                      {contractor.years_in_business && (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          {contractor.years_in_business} years in business
                        </div>
                      )}
                    </div>
                    
                    {contractor.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                        {contractor.description}
                      </p>
                    )}
                    
                    {contractor.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {contractor.services.slice(0, 3).map((service, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-400"
                          >
                            {service}
                          </span>
                        ))}
                        {contractor.services.length > 3 && (
                          <span className="px-2 py-1 text-xs text-slate-500">
                            +{contractor.services.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openInquiry(contractor)}
                        className="flex-1 bg-sky-500 hover:bg-sky-600"
                        size="sm"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Contact
                      </Button>
                      <Button
                        onClick={() => openReview(contractor)}
                        variant="outline"
                        size="sm"
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                      {contractor.website && (
                        <Button
                          onClick={() => window.open(contractor.website, '_blank')}
                          variant="outline"
                          size="sm"
                          className="border-slate-700 hover:bg-slate-800"
                        >
                          <Globe className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-12 text-center"
            >
              <p className="text-slate-400 mb-4">Are you a contractor in {stateData.name}?</p>
              <Button
                onClick={() => navigate('/contractors/register')}
                variant="outline"
                className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
              >
                <Building2 className="w-4 h-4 mr-2" />
                List Your Business Free
              </Button>
            </motion.div>
          </>
        )}
      </main>

      {/* Inquiry Modal */}
      <AnimatePresence>
        {showInquiryModal && selectedContractor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowInquiryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">Contact {selectedContractor.business_name}</h2>
                  <p className="text-sm text-slate-400">Send an inquiry to this contractor</p>
                </div>
                <button
                  onClick={() => setShowInquiryModal(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Your Name *</label>
                  <Input
                    value={inquiryForm.name}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Email *</label>
                  <Input
                    type="email"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Service Needed</label>
                  <Input
                    value={inquiryForm.service_needed}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, service_needed: e.target.value }))}
                    placeholder="e.g., AC repair, furnace installation"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Message *</label>
                  <textarea
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Describe what you need help with..."
                    required
                    rows={4}
                    className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-white resize-none"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={submittingInquiry}
                  className="w-full bg-sky-500 hover:bg-sky-600"
                >
                  {submittingInquiry ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Modal */}
      <AnimatePresence>
        {showReviewModal && selectedContractor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{selectedContractor.business_name}</h2>
                  <p className="text-sm text-slate-400">Rate and review this contractor</p>
                </div>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Existing Reviews */}
              {contractorReviews && contractorReviews.reviews.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4 p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-3xl font-bold text-yellow-400">
                      {contractorReviews.stats.avg_rating?.toFixed(1) || '0.0'}
                    </div>
                    <div>
                      <StarRating rating={Math.round(contractorReviews.stats.avg_rating || 0)} />
                      <p className="text-sm text-slate-400">{contractorReviews.stats.total} reviews</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {contractorReviews.reviews.slice(0, 5).map((review) => (
                      <div key={review.review_id} className="p-3 bg-slate-800/30 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <StarRating rating={review.rating} size="sm" />
                          <span className="text-sm font-medium">{review.reviewer_name}</span>
                        </div>
                        {review.title && <p className="font-medium text-sm">{review.title}</p>}
                        <p className="text-sm text-slate-400">{review.comment}</p>
                        
                        {/* Contractor Response */}
                        {review.contractor_response && (
                          <div className="mt-2 pl-3 border-l-2 border-sky-500/50">
                            <p className="text-xs text-sky-400 font-medium mb-1">
                              Response from {review.responded_by || 'Business'}
                            </p>
                            <p className="text-sm text-slate-300">{review.contractor_response}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Review Form */}
              <form onSubmit={handleReviewSubmit} className="space-y-4 border-t border-slate-700 pt-4">
                <h3 className="font-medium">Write a Review</h3>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Your Rating *</label>
                  <StarRating 
                    rating={reviewForm.rating} 
                    onRate={(r) => setReviewForm(prev => ({ ...prev, rating: r }))}
                    interactive
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Your Name *</label>
                    <Input
                      value={reviewForm.reviewer_name}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, reviewer_name: e.target.value }))}
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Email *</label>
                    <Input
                      type="email"
                      value={reviewForm.reviewer_email}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, reviewer_email: e.target.value }))}
                      required
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Review Title</label>
                  <Input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Great service!"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Your Review *</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Share your experience..."
                    required
                    rows={3}
                    className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-white resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Service Used</label>
                  <Input
                    value={reviewForm.service_used}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, service_used: e.target.value }))}
                    placeholder="e.g., Furnace repair"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={submittingReview || !reviewForm.rating}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  {submittingReview ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4 mr-2" />
                      Submit Review
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-500">
            Forged in Alaska. Serving All 50. © 2026 Alaska AI Services.
          </p>
        </div>
      </footer>
    </div>
  );
}
